import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FirstCustomerToolkit } from "@/components/toolkit/FirstCustomerToolkit";
import { ArrowLeft, Bookmark, Heart, Rocket, Info, Check, ArrowRight, ArrowDown, Quote, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/common";
import { IdeaBadges } from "@/components/IdeaCard";
import StartBusinessDialog from "@/components/StartBusinessDialog";
import { shortRs, fitReasons, REVENUE_DISCLAIMER } from "@/lib/ideas";

const firstSentence = (t = "") => (t.match(/^.*?[.!?](\s|$)/)?.[0] || t).trim();

function Snapshot({ idea }) {
  const items = [["Startup", shortRs(idea.startup_cost)], ["Difficulty", idea.difficulty], ["Time", idea.time_required], ["First customer", `${idea.first_customer_difficulty} difficulty`],
    ["Competition", idea.competition], ["Margin", idea.margin]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-4 stagger" data-testid="idea-snapshot">
      <div className="col-span-2 rounded-2xl bg-[#1DB99A] text-white p-4" data-testid="snapshot-monthly-revenue">
        <div className="text-[11px] font-semibold text-white/80">Monthly revenue potential</div>
        <div className="font-display font-extrabold text-xl mt-0.5">{shortRs(idea.revenue_monthly_text)}</div>
      </div>
      {items.map(([k, v]) => (
        <div key={k} className="sb-card p-3.5" data-testid={`snapshot-${k.toLowerCase().replace(" ", "-")}`}><div className="text-[11px] font-semibold text-[#8C9BA5]">{k}</div><div className="font-display font-bold text-sm mt-0.5 leading-snug">{v}</div></div>
      ))}
    </div>
  );
}

function Section({ title, children, testid }) {
  return <section className="p-5 sm:p-6" data-testid={testid}><h2 className="sb-eyebrow mb-2">{title}</h2>{children}</section>;
}

function BuyingFlow({ idea }) {
  const steps = [["Customer problem", idea.problem], ["Why they pay", firstSentence(idea.why_customers_buy)], ["Your offer", firstSentence(idea.involves)], ["How you reach them", idea.find_customers]];
  return (
    <div className="grid sm:grid-cols-4 gap-2 mt-4" data-testid="buying-flow">
      {steps.map(([k, v], i) => (
        <div key={k} className="relative">
          <div className="sb-card p-4 h-full"><div className="text-[10px] font-bold text-[#1DB99A]">0{i + 1}</div><div className="font-display font-extrabold text-sm">{k}</div><p className="text-xs text-[#5C6B73] mt-1.5 leading-relaxed">{v}</p></div>
          {i < 3 && <><ArrowRight className="hidden sm:block absolute -right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#1DB99A] z-10" /><ArrowDown className="sm:hidden mx-auto h-4 w-4 text-[#1DB99A] my-1" /></>}
        </div>
      ))}
    </div>
  );
}

function RevenueCard({ idea }) {
  const lo = idea.estimated_monthly_revenue_low, hi = idea.estimated_monthly_revenue_high;
  const max = Math.max(100000, (hi || 0) * 1.15);
  return (
    <section className="rounded-2xl bg-[#1E2B3C] text-white p-5 sm:p-7 mt-5" data-testid="revenue-potential-card">
      <div className="flex items-center justify-between">
        <div className="sb-eyebrow text-[#1DB99A]">Revenue potential</div>
        <TooltipProvider><Tooltip><TooltipTrigger className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center" data-testid="revenue-info-btn"><Info className="h-4 w-4" /></TooltipTrigger>
          <TooltipContent className="max-w-xs">These are indicative gross-revenue scenarios (sales before costs) — not profit or guaranteed income.</TooltipContent></Tooltip></TooltipProvider>
      </div>
      <div className="grid sm:grid-cols-2 gap-5 mt-3">
        <div><div className="text-xs text-white/60">Estimated monthly revenue</div><div className="font-display text-2xl sm:text-3xl font-extrabold" data-testid="revenue-monthly">{idea.revenue_monthly_text || "—"} <span className="text-sm font-semibold text-white/60">/ month</span></div></div>
        <div><div className="text-xs text-white/60">Estimated annual revenue</div><div className="font-display text-xl sm:text-2xl font-extrabold" data-testid="revenue-annual">{idea.revenue_annual_text || "—"} <span className="text-sm font-semibold text-white/60">/ year</span></div></div>
      </div>
      {lo != null && (
        <div className="mt-6" data-testid="revenue-range">
          <div className="text-[11px] font-bold uppercase tracking-wider text-white/60 mb-2">Monthly potential range</div>
          <div className="relative h-3 rounded-full bg-white/10">
            <div className="absolute h-full rounded-full bg-[#1DB99A]" style={{ left: `${(100 * lo) / max}%`, width: `${Math.max(3, (100 * (hi - lo)) / max)}%` }} />
          </div>
          <div className="relative h-10 mt-1 text-xs">
            <div className="absolute -translate-x-1/2 text-center" style={{ left: `${(100 * lo) / max}%` }}><div className="text-white/50">Low scenario</div><b>{shortRs(`Rs ${lo.toLocaleString("en-US")}`)}</b></div>
            <div className="absolute -translate-x-1/2 text-center" style={{ left: `${Math.min(92, (100 * hi) / max)}%` }}><div className="text-white/50">Higher activity</div><b>{shortRs(`Rs ${hi.toLocaleString("en-US")}`)}</b></div>
          </div>
        </div>
      )}
      {idea.projection_basis && (
        <div className="mt-5 rounded-xl bg-white/5 p-4" data-testid="projection-basis">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#1DB99A]">How this projection works</div>
          <p className="text-sm text-white/80 mt-1">{idea.projection_basis}</p>
        </div>
      )}
      <p className="text-[11px] text-white/50 mt-4 leading-relaxed" data-testid="revenue-disclaimer">{REVENUE_DISCLAIMER}</p>
    </section>
  );
}

function Opportunity({ idea }) {
  const rows = [["Startup cost", idea.startup_cost], ["Potential monthly revenue", idea.revenue_monthly_text], ["Difficulty", idea.difficulty], ["Time required", idea.time_required], ["Potential margin", idea.margin]];
  return (
    <section className="sb-card p-5 mt-5" data-testid="opportunity-card">
      <div className="sb-eyebrow mb-3">Startup vs revenue</div>
      <dl className="divide-y divide-[#EADEC9]">{rows.map(([k, v]) => <div key={k} className="flex justify-between gap-4 py-2.5 text-sm"><dt className="text-[#5C6B73]">{k}</dt><dd className="font-semibold text-right">{v}</dd></div>)}</dl>
    </section>
  );
}

function SellSection({ idea }) {
  const m = (idea.marketing_angle || "").match(/^Hook:\s*[“"](.+?)[”"]\.?\s*(.*)$/s);
  const hook = m?.[1];
  const rest = m ? m[2] : idea.marketing_angle;
  const where = (idea.find_customers || "").split(/,|;/).map((x) => x.trim()).filter(Boolean);
  const steps = (idea.first_steps || "").split("→").map((x) => x.trim()).filter(Boolean);
  return (
    <section className="sb-card p-5 sm:p-6 mt-5" data-testid="how-to-sell">
      <div className="sb-eyebrow">How to sell this business</div>
      {hook && <div className="mt-3 rounded-2xl bg-[#E8F8F4] p-4 flex gap-3" data-testid="marketing-hook"><Quote className="h-5 w-5 text-[#1DB99A] shrink-0" /><p className="font-display font-extrabold text-lg leading-snug">{hook}</p></div>}
      {rest && <p className="text-[15px] mt-3 leading-relaxed">{rest}</p>}
      {where.length > 0 && <><div className="text-xs font-bold mt-5 mb-2">Where to find customers</div><div className="flex flex-wrap gap-2">{where.map((w) => <span key={w} className="rounded-full border border-[#EADEC9] bg-[#FBF8F2] px-3 py-1.5 text-xs font-semibold">{w}</span>)}</div></>}
      {steps.length > 0 && (
        <div className="mt-5" data-testid="first-marketing-move">
          <div className="text-xs font-bold mb-2">First marketing move</div>
          <ol className="space-y-2">{steps.map((s, i) => <li key={i} className="flex gap-3 text-sm items-center"><span className="h-7 w-7 shrink-0 rounded-full bg-[#1DB99A] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>{s.charAt(0).toUpperCase() + s.slice(1)}</li>)}</ol>
        </div>
      )}
    </section>
  );
}

const CTA_LIST = ["Validation steps", "Offer creation", "Registration checklist", "Business setup checklist", "Launch plan", "Progress tracking", "Lessons with audio", "AI business guidance"];

export default function IdeaReport() {
  const { number } = useParams();
  const { can, user } = useAuth();
  const nav = useNavigate();
  const [idea, setIdea] = useState(null);
  const [start, setStart] = useState(false);
  useEffect(() => { setIdea(null); api.get(`/ideas/${number}`).then((r) => setIdea(r.data)); }, [number]);
  const toggle = async (kind) => {
    const { data } = await api.post(`/ideas/${number}/toggle/${kind}`);
    setIdea((i) => ({ ...i, [kind]: data.active }));
    toast.success(data.active ? (kind === "saved" ? "Idea saved" : "Added to favorites") : "Removed");
  };
  if (!idea) return <Loader />;
  const fit = fitReasons(idea, user.preferences);
  return (
    <div className="max-w-3xl" data-testid="idea-detail">
      <Link to="/ideas" className="inline-flex items-center gap-1 text-sm font-semibold text-[#5C6B73] mb-4" data-testid="idea-back-link"><ArrowLeft className="h-4 w-4" /> All ideas</Link>
      <div className="sb-card p-6 sm:p-8 fade-up">
        <div className="sb-eyebrow">Sidebizz Ideas · #{idea.number} · {idea.category}</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold mt-2" data-testid="idea-detail-name">{idea.name}</h1>
        <div className="mt-4"><IdeaBadges tags={idea.tags} /></div>
        <div className="flex gap-2 mt-5">
          <button onClick={() => toggle("saved")} className={`sb-btn-ghost text-sm ${idea.saved ? "border-[#1DB99A] text-[#179B80]" : ""}`} data-testid="idea-detail-save-btn"><Bookmark className={`h-4 w-4 ${idea.saved ? "fill-[#1DB99A] text-[#1DB99A]" : ""}`} />{idea.saved ? "Saved" : "Save idea"}</button>
          <button onClick={() => toggle("favorite")} className="sb-btn-ghost text-sm" data-testid="idea-detail-fav-btn"><Heart className={`h-4 w-4 ${idea.favorite ? "fill-[#E05A47] text-[#E05A47]" : ""}`} />Favorite</button>
        </div>
      </div>
      <Snapshot idea={idea} />

      {fit.length > 0 && (
        <section className="sb-card p-5 mt-5 border-[#1DB99A]" data-testid="why-fit-you">
          <div className="sb-eyebrow text-[#179B80] flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" />Why this could fit you</div>
          <ul className="mt-3 space-y-1.5">{fit.map((r) => <li key={r} className="flex gap-2 text-sm font-medium"><Check className="h-4 w-4 text-[#1DB99A] mt-0.5" />{r}</li>)}</ul>
          <p className="text-[11px] text-[#8C9BA5] mt-2">Based on your Find My Business answers.</p>
        </section>
      )}

      <div className="sb-card mt-5 divide-y divide-[#EADEC9]">
        <Section title="What this business is" testid="idea-section-involves"><p className="text-[15px] leading-relaxed">{idea.involves}</p></Section>
        <Section title="Problem solved" testid="idea-section-problem"><p className="text-[15px] leading-relaxed">{idea.problem}</p></Section>
        <Section title="Target customer" testid="idea-section-target"><p className="text-[15px] leading-relaxed">{idea.target}</p></Section>
      </div>

      {idea.why_customers_buy && (
        <section className="rounded-2xl border-2 border-[#1DB99A] bg-[#E8F8F4] p-5 sm:p-7 mt-5" data-testid="why-customers-buy">
          <div className="sb-eyebrow text-[#179B80]">Why customers would buy</div>
          <p className="font-display text-lg sm:text-xl font-bold leading-snug mt-2 text-[#1E2B3C]">{idea.why_customers_buy}</p>
        </section>
      )}
      <BuyingFlow idea={idea} />
      <RevenueCard idea={idea} />
      <Opportunity idea={idea} />
      <SellSection idea={idea} />

      <div className="sb-card mt-5 divide-y divide-[#EADEC9]">
        <Section title="Why this can work in Mauritius" testid="idea-section-why_mauritius"><p className="text-[15px] leading-relaxed">{idea.why_mauritius}</p></Section>
        <Section title="How to make it profitable" testid="idea-section-profitability"><p className="text-[15px] leading-relaxed">{idea.profitability}</p></Section>
      </div>

      <div className="mt-6"><FirstCustomerToolkit number={Number(number)} /></div>

      <div className="rounded-3xl bg-[#1E2B3C] text-white p-6 sm:p-8 mt-6" data-testid="idea-build-cta">
        <h2 className="text-2xl font-extrabold">Ready to build this business?</h2>
        <p className="text-sm text-white/60 mt-1">Sidebizz can guide you from idea to launch. You'll get:</p>
        <ul className="grid grid-cols-2 gap-x-4 gap-y-2 mt-4 text-sm">{CTA_LIST.map((x) => <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-[#1DB99A] mt-0.5 shrink-0" />{x}</li>)}</ul>
        <button onClick={() => (can("builder") ? setStart(true) : nav(`/upgrade?idea=${number}`))} className="sb-btn-primary mt-6 w-full sm:w-auto sm:px-10 text-base" data-testid="build-this-business-btn"><Rocket className="h-5 w-5" /> BUILD THIS BUSINESS</button>
      </div>
      <StartBusinessDialog open={start} onOpenChange={setStart} idea={idea} />
    </div>
  );
}
